import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "./StoreLayout";
import api, { money, fileUrl, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, ExternalLink, Upload, CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { slug, store, cart, clearCart, cartCount } = useStore();
  const navigate = useNavigate();
  const fileRef = useRef();
  const ful = store.fulfillment || {};
  const methods = [["shipping", "Shipping", ful.shipping_fee], ["delivery", "Local delivery", ful.delivery_fee], ["pickup", "Local pickup", 0]].filter(([k]) => ful[k]);
  const [fulfillment, setFulfillment] = useState(methods[0]?.[0] || "pickup");
  const [payment, setPayment] = useState(store.payment_methods?.[0]?.type || "");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [codConfirmed, setCodConfirmed] = useState(false);
  const [paidClicked, setPaidClicked] = useState(false);
  const [proofPath, setProofPath] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [claimed, setClaimed] = useState("");
  const [txnRef, setTxnRef] = useState("");
  const [custNote, setCustNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const clientToken = useMemo(() => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), []);
  const [linkInfo, setLinkInfo] = useState(null);

  useEffect(() => { if (cartCount === 0) navigate(`/store/${slug}`); }, [cartCount, slug, navigate]);

  const pmSel = store.payment_methods?.find((p) => p.type === payment);
  const codSel = payment === "cod";
  useEffect(() => {
    if (!payment || codSel || cartCount === 0) { setLinkInfo(null); return; }
    api.post("/shop/payment-link", {
      items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      fulfillment_method: fulfillment, payment_method: payment,
    }).then((r) => setLinkInfo(r.data)).catch(() => setLinkInfo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, fulfillment]);

  if (cartCount === 0) return null;

  const pm = store.payment_methods?.find((p) => p.type === payment);
  const isCod = payment === "cod";
  const requireProof = !!pm && !isCod && pm.require_proof !== false;
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const fee = fulfillment === "shipping" ? Number(ful.shipping_fee || 0) : fulfillment === "delivery" ? Number(ful.delivery_fee || 0) : 0;
  const total = subtotal + fee;

  const onProof = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const path = await uploadFile(f);
      setProofPath(path);
      setProofPreview(f.type === "application/pdf" ? null : URL.createObjectURL(f));
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const place = async () => {
    if (!payment) { toast.error("Select a payment method"); return; }
    if (fulfillment === "shipping" && !address.trim()) { toast.error("Enter a shipping address"); return; }
    if (isCod && !codConfirmed) { toast.error("Please confirm the Cash on Delivery order"); return; }
    if (requireProof && !proofPath) { toast.error("Please upload your payment proof"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/shop/orders", {
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_method: payment, fulfillment_method: fulfillment,
        shipping_address: address, notes,
        proof_file_path: proofPath, claimed_amount: claimed ? Number(claimed) : null,
        transaction_ref: txnRef, customer_note: custNote, client_token: clientToken,
        reserved_order_number: linkInfo?.order_number,
      });
      clearCart();
      toast.success("Order placed!");
      navigate(`/store/${slug}/orders/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5 rounded-xl border-border">
            <h3 className="font-semibold mb-4">Fulfillment</h3>
            <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="space-y-2">
              {methods.map(([k, label, f]) => (
                <label key={k} className="flex items-center justify-between border border-border rounded-lg p-3 cursor-pointer hover:border-primary/50" data-testid={`fulfillment-${k}`}>
                  <div className="flex items-center gap-3"><RadioGroupItem value={k} /> <span className="font-medium">{label}</span></div>
                  <span className="text-sm text-muted-foreground">{Number(f) > 0 ? money(f) : "Free"}</span>
                </label>
              ))}
            </RadioGroup>
            {fulfillment === "shipping" && (
              <div className="mt-4 space-y-2"><Label>Shipping address</Label><Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} data-testid="checkout-address" /></div>
            )}
          </Card>

          <Card className="p-5 rounded-xl border-border">
            <h3 className="font-semibold mb-4">Payment method</h3>
            <RadioGroup value={payment} onValueChange={(v) => { setPayment(v); setPaidClicked(false); setProofPath(null); setProofPreview(null); }} className="space-y-2">
              {store.payment_methods?.map((p) => (
                <label key={p.type} className="flex items-center gap-3 border border-border rounded-lg p-3 cursor-pointer hover:border-primary/50" data-testid={`payment-${p.type}`}>
                  <RadioGroupItem value={p.type} /> <span className="font-medium">{p.label}</span>
                </label>
              ))}
            </RadioGroup>

            {isCod ? (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm font-medium text-amber-800">Payment is due upon delivery.</p>
                <p className="text-xs text-amber-700 mt-0.5">{pm?.instructions || "You'll pay in cash when your order arrives."}</p>
                <label className="flex items-center gap-2 mt-3 text-sm text-amber-900 cursor-pointer">
                  <input type="checkbox" checked={codConfirmed} onChange={(e) => setCodConfirmed(e.target.checked)} data-testid="cod-confirm-checkbox" className="h-4 w-4 rounded" />
                  I understand and will pay on delivery.
                </label>
              </div>
            ) : pmSel && (
              <div className="mt-4 space-y-3 rounded-lg border border-border p-4 bg-secondary/20" data-testid="payment-details">
                <p className="text-sm font-semibold">Send {money(total)} via {pmSel.label}</p>
                {linkInfo?.order_number && <p className="text-xs text-muted-foreground">Reference: Drelik Order {linkInfo.order_number}</p>}
                {linkInfo?.recipient_info && <p className="text-sm text-muted-foreground whitespace-pre-wrap"><span className="font-medium text-foreground">Send to:</span> {linkInfo.recipient_info}</p>}
                {linkInfo?.instructions && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{linkInfo.instructions}</p>}
                {(linkInfo?.qr_data_uri || linkInfo?.uploaded_qr_url) && <img src={linkInfo.qr_data_uri || fileUrl(linkInfo.uploaded_qr_url)} alt="Payment QR" className="h-40 w-40 rounded-lg border border-border object-contain bg-white" data-testid="payment-qr" />}
                {linkInfo?.payment_url && (
                  <a href={linkInfo.payment_url} target="_blank" rel="noreferrer" data-testid="open-payment-link">
                    <Button type="button" variant="outline" className="rounded-lg"><ExternalLink className="h-4 w-4 mr-1.5" /> Open {pmSel.label}</Button>
                  </a>
                )}
                {!linkInfo?.payment_url && !linkInfo?.recipient_info && <p className="text-sm text-muted-foreground">Contact the store for payment details.</p>}
                <div className="pt-2 border-t border-border">
                  {!paidClicked ? (
                    <Button type="button" onClick={() => setPaidClicked(true)} className="rounded-lg" data-testid="completed-payment-btn">I've completed my payment</Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label>Amount you paid</Label>
                        <Input type="number" step="0.01" value={claimed} onChange={(e) => setClaimed(e.target.value)} placeholder={String(total.toFixed(2))} className="h-10" data-testid="claimed-amount-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Transaction / reference # (optional)</Label>
                        <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} className="h-10" data-testid="txn-ref-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{requireProof ? "Payment proof (required)" : "Payment proof (optional)"}</Label>
                        {proofPath ? (
                          <div className="flex items-center gap-3">
                            {proofPreview ? <img src={proofPreview} alt="Proof" className="h-16 w-16 rounded-lg border border-border object-cover" data-testid="proof-preview" /> : <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> File attached</span>}
                            <Button type="button" variant="ghost" size="sm" onClick={() => { setProofPath(null); setProofPreview(null); fileRef.current && (fileRef.current.value = ""); }} data-testid="replace-proof-btn">Replace</Button>
                          </div>
                        ) : (
                          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-lg" data-testid="upload-proof-btn">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload proof
                          </Button>
                        )}
                        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" hidden onChange={onProof} />
                      </div>
                      <Textarea value={custNote} onChange={(e) => setCustNote(e.target.value)} rows={2} placeholder="Note to the store (optional)" data-testid="customer-note-input" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5 rounded-xl border-border">
            <Label>Order notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-2" data-testid="checkout-notes" />
          </Card>
        </div>

        <div>
          <Card className="p-5 rounded-xl border-border sticky top-24">
            <h3 className="font-semibold mb-4">Order summary</h3>
            <div className="space-y-2 mb-4 max-h-52 overflow-y-auto">
              {cart.map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground truncate mr-2">{i.name} × {i.quantity}</span>
                  <span>{money(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-border pt-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground capitalize">{fulfillment} fee</span><span>{money(fee)}</span></div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-border"><span>Total</span><span data-testid="checkout-total">{money(total)}</span></div>
            </div>
            <Button onClick={place} disabled={loading || (!isCod && requireProof && !proofPath)} className="w-full h-12 rounded-full mt-4" data-testid="place-order-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit order"}
            </Button>
            {!isCod && requireProof && !proofPath && <p className="text-xs text-muted-foreground mt-2 text-center">Upload payment proof to submit.</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
